"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { PageTransition } from "@/components/ui-enhanced/PageTransition";
import PublicLayout from "@/components/PublicLayout";
import { usePageContent } from "@/hooks/usePageContent";
import {
  ShieldCheck,
  Accessibility,
  Eye,
  Volume2,
  Keyboard,
  ScanEye,
  Speech,
  Flame,
} from "lucide-react";
import { useMetadata } from "@/hooks/useMetadata";

type AccessibilitySection = {
  id: string;
  title: string;
  content: React.ReactNode;
};

export const ACCESSIBILITY_COPY: AccessibilitySection[] = [
  {
    id: "our-commitment",
    title: "1. Our Commitment",
    content: (
      <p className="leading-relaxed">
        UniUI is designed to be inclusive and accessible to all Nigerian
        university students, including those with visual, auditory, motor, or
        cognitive disabilities. We follow the Web Content Accessibility
        Guidelines (WCAG) 2.1 Level AA standards and continuously improve the
        platform to meet evolving accessibility requirements.
      </p>
    ),
  },
  {
    id: "accessibility-features",
    title: "2. Accessibility Features",
    content: (
      <ul className="space-y-4 ml-2">
        <li className="flex items-start gap-3">
          <Eye className="mt-1 h-4.5 w-4.5 text-y shrink-0" />
          <span className="leading-relaxed text-sm">
            <strong>High-contrast mode</strong> — toggle in{" "}
            <strong>Settings → Accessibility</strong> to increase visual
            contrast for users with low vision.
          </span>
        </li>
        <li className="flex items-start gap-3">
          <ScanEye className="mt-1 h-4.5 w-4.5 text-y shrink-0" />
          <span className="leading-relaxed text-sm">
            <strong>Large text option</strong> — adjustable font sizing for
            improved readability.
          </span>
        </li>
        <li className="flex items-start gap-3">
          <Keyboard className="mt-1 h-4.5 w-4.5 text-y shrink-0" />
          <span className="leading-relaxed text-sm">
            <strong>Keyboard-navigable interface</strong> — all features are
            accessible without a mouse, with visible focus indicators and
            logical tab order.
          </span>
        </li>
        <li className="flex items-start gap-3">
          <ShieldCheck className="mt-1 h-4.5 w-4.5 text-y shrink-0" />
          <span className="leading-relaxed text-sm">
            <strong>Screen-reader-friendly markup</strong> — ARIA labels,
            semantic HTML, and meaningful alt text throughout the platform.
          </span>
        </li>
        <li className="flex items-start gap-3">
          <Speech className="mt-1 h-4.5 w-4.5 text-y shrink-0" />
          <span className="leading-relaxed text-sm">
            <strong>Text-to-speech for answers</strong> — audio summaries of
            AI-generated responses for users with visual impairments.
          </span>
        </li>
        <li className="flex items-start gap-3">
          <Flame className="mt-1 h-4.5 w-4.5 text-y shrink-0" />
          <span className="leading-relaxed text-sm">
            <strong>Reduce motion option</strong> — users sensitive to
            animations can disable motion effects in Settings.
          </span>
        </li>
        <li className="flex items-start gap-3">
          <Volume2 className="mt-1 h-4.5 w-4.5 text-y shrink-0" />
          <span className="leading-relaxed text-sm">
            <strong>Focus indicators</strong> — clear visual indicators for
            keyboard navigation on all interactive elements.
          </span>
        </li>
      </ul>
    ),
  },
  {
    id: "known-limitations",
    title: "3. Known Limitations",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>
          While we strive for full accessibility, some areas may still require
          improvement:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>
            Complex LaTeX formulas may not be fully accessible to screen
            readers. We are exploring alternative accessible representations for
            mathematical notation.
          </li>
          <li>
            Some third‑party components (interactive charts, embedded tools) may
            have limited keyboard support. We are working with vendors to
            improve their accessibility.
          </li>
          <li>
            Audio transcription accuracy varies for speakers with heavy accents
            or in environments with significant background noise. We are
            improving our speech-to-text models for Nigerian English variants.
          </li>
        </ul>
        <p>
          We are actively working to address these limitations and appreciate
          your patience and feedback.
        </p>
      </div>
    ),
  },
  {
    id: "feedback-and-assistance",
    title: "4. Feedback & Assistance",
    content: (
      <p className="leading-relaxed">
        If you encounter unknown accessibility barriers while using UniUI,
        please let us know. We take accessibility feedback seriously and will
        work to resolve issues promptly. You can report barriers via the{" "}
        <Link href="/contact" className="text-y font-bold hover:underline">
          Contact page
        </Link>{" "}
        or by emailing{" "}
        <a
          href="mailto:support@uniui.com.ng"
          className="text-y font-bold hover:underline"
        >
          support@uniui.com.ng
        </a>
        . Please include details about the barrier you encountered, the device
        and assistive technology you were using, and unknown relevant
        screenshots.
      </p>
    ),
  },
  {
    id: "third-party-content",
    title: "5. Third-Party Content",
    content: (
      <p className="leading-relaxed">
        Some features rely on third-party services, including Cloudinary for
        image hosting, Kroki for diagram rendering, and embedded YouTube
        transcripts. We encourage these providers to maintain accessibility
        standards and WCAG compliance, but we cannot guarantee their adherence.
        If you encounter an accessibility issue with a third-party component,
        please report it so we can escalate to the relevant provider.
      </p>
    ),
  },
  {
    id: "ongoing-efforts",
    title: "6. Ongoing Efforts",
    content: (
      <p className="leading-relaxed">
        Accessibility is an ongoing commitment. We regularly audit the platform,
        test with assistive technologies (including NVDA, JAWS, VoiceOver, and
        switch devices), and update our practices based on user feedback and
        evolving WCAG standards. Our goal is to ensure that every Nigerian
        university student can use UniUI effectively, regardless of ability.
      </p>
    ),
  },
  {
    id: "last-updated",
    title: "7. Last Updated",
    content: (
      <p className="leading-relaxed">
        This Accessibility Statement was last updated in{" "}
        <strong>June 2026</strong>.
      </p>
    ),
  },
];

export default function AccessibilityStatementPage() {
  const copy = usePageContent("accessibility", ACCESSIBILITY_COPY);
  useMetadata({ key: "accessibility" });
  return (
    <PublicLayout>
      <PageTransition>
        <div className="min-h-screen bg-background">
          <div className="max-w-6xl mx-auto px-6 py-12">
            <motion.header
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12 space-y-3"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <Accessibility className="w-8 h-8 text-y" />
                <h1 className="display text-4xl md:text-5xl tracking-tight">
                  Accessibility Statement
                </h1>
              </div>
              <p className="text-muted-foreground font-medium max-w-2xl mx-auto leading-relaxed">
                We are committed to making UniUI accessible to all students,
                regardless of ability.
              </p>
              <p className="text-xs text-muted-foreground font-bold uppercase tracking-widest">
                Last_updated: June 2026
              </p>
            </motion.header>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-8 space-y-10">
                {copy.map((section, index) => (
                  <motion.section
                    key={section.id}
                    id={section.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.04 }}
                    className="scroll-mt-24"
                  >
                    <div className="matte-metal p-8 md:p-10 rounded-[2rem] space-y-5">
                      <h2 className="display text-xl md:text-2xl tracking-tight text-foreground">
                        {section.title}
                      </h2>
                      <div className="text-sm text-muted-foreground leading-relaxed font-medium space-y-4">
                        {section.content}
                      </div>
                    </div>
                  </motion.section>
                ))}
              </div>

              <aside className="hidden lg:block lg:col-span-4">
                <div className="sticky top-24 space-y-4">
                  <div className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground mb-2">
                    Settings Quick Links
                  </div>
                  <nav className="space-y-1">
                    <Link
                      href="/settings/accessibility"
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-widest text-muted-foreground hover:bg-card hover:text-foreground transition-all duration-200 group"
                    >
                      <ShieldCheck className="w-4 h-4 text-y" />
                      <span>Accessibility Settings</span>
                    </Link>
                    <Link
                      href="/settings"
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-widest text-muted-foreground hover:bg-card hover:text-foreground transition-all duration-200"
                    >
                      <span>All Settings</span>
                    </Link>
                    <Link
                      href="/contact"
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-widest text-muted-foreground hover:bg-card hover:text-foreground transition-all duration-200"
                    >
                      <span>Contact Support</span>
                    </Link>
                  </nav>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </PageTransition>
    </PublicLayout>
  );
}
